import { useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import dayjs from "dayjs";

import { InboundLayout, InboundHeaderCard } from "ui-components/Layout";
import {
  InboundSubmitButton,
  InboundDetailAddButton,
  InboundDetailEditButton,
} from "ui-components/Form";

import PO from "./components/PO";
import { headerRender, headerValues, headerValidation } from "./po-schema";
import { detailRender, detailValues, detailValidation } from "./po-details-schema";
import { useCustomerValue } from "providers/CustomerContext";
import PODetail from "./components/PODetails";
import { Success, Error_, InfoDialog } from "ui-components/Alert";
import { dateValidation } from "layouts/views/common/validation";
import { poColumns } from "./columns";
import { useMutation } from "@tanstack/react-query";
import { createPO } from "services/data.inbound.service";
import BasicTable2 from "ui-components/tables/BasicTable2";
import { deleteDetail, getTotalQuantity, skuValidation_, updateDetail } from "../common/common";
import { POCaption } from "../components/Caption";
import { useVendorOptionsWithName } from "hooks/useQueryHook";
import { useNavigate } from "react-router-dom";

const defaultValues = {
  ...headerValues,
  poDate: dayjs(new Date()).format("YYYY-MM-DD"),
  poType: { label: "Standard", value: "S" },
  status: "Open",
};

function AddPO() {
  const navigate = useNavigate();
  const { customerCode } = useCustomerValue();
  const [vendorOptions, setVendorOptions] = useState([]);
  const formValues = { ...defaultValues, customerCode };
  const [alert, setAlert] = useState(null);
  const [details, setDetails] = useState([]);
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);
  const createPoMutation = useMutation(createPO, {});

  useVendorOptionsWithName({
    customerCode,
    onSuccess: (data) => {
      const vendors = data.data.data;
      if (vendors.length > 0) {
        setValue("vendorCode", vendors[0].vendorCode);
        setValue("sellerCompany", vendors[0].vendShortName);
      }
      setVendorOptions(vendors.map((x) => x.vendorCode));
    },
    onError,
  });

  const form = useForm({
    defaultValues: formValues,
    values: formValues,
    resolver: yupResolver(headerValidation),
  });
  const {
    control,
    watch,
    formState: { errors },
    setValue,
    getValues,
    trigger,
  } = form;

  const headerProps = { headerRender, vendorOptions, control, setValue, errors };

  const [vendorCode] = watch(["vendorCode"]);

  const detailProps = { details, setDetails, values: { customerCode, vendorCode } };

  const createStatus = createPoMutation.status === "loading";

  const onSubmit = async () => {
    const isValid = await trigger();
    if (!isValid) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      return;
    }

    const { poType, ...data } = getValues();

    if (details.length === 0) {
      setAlert(<Error_ content="Invalid Purchase Order Details" onChange={setAlert} />);
      return;
    }

    const isDateValid = dateValidation(details, data);
    if (isDateValid) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      setAlert(<Error_ content="Invalid ETA Date must greater than PO Date" onChange={setAlert} />);
      return;
    }

    const req = {
      ...data,
      poType: poType.value,
      poDetails: details.map((d) => ({ ...d, status: "O", poUOM: d.poUOM.label })),
    };

    createPoMutation.mutate(req, {
      onSuccess: (data) => navigate("/inbound/po", { state: data.data.message }),
      onError: (data) => {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        setAlert(<Error_ data={data} onChange={setAlert} />);
      },
    });
  };
  return (
    <InboundLayout lg={12}>
      {alert}
      <InboundHeaderCard>
        <form noValidate>
          <PO {...headerProps} />
        </form>
      </InboundHeaderCard>

      <Detail {...detailProps} />
      <InboundSubmitButton
        label={createStatus ? "Submit..." : "Submit"}
        onClick={onSubmit}
        disabled={details.length === 0 || createStatus}
      />
    </InboundLayout>
  );
}

const Detail = ({ details, setDetails, values }) => {
  const columns = [
    {
      field: "actions",
      headerName: "Action",
      renderActions: ["Delete"],
      type: "actions",
    },
    ...poColumns,
  ];
  const [alert, setAlert] = useState(null);
  const [formValues, setFormValues] = useState(null);
  const [dialog, setDialog] = useState(null);

  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);

  const lineNo = formValues ? formValues.lineNo : details.length + 1;

  const form = useForm({
    defaultValues: { ...detailValues, lineNo },
    values: formValues || { ...detailValues, lineNo, status: "Open" },
    resolver: yupResolver(detailValidation),
  });
  const {
    control,
    watch,
    formState: { errors, isDirty },
    setValue,
    getValues,
    handleSubmit,
    reset,
  } = form;
  const [skuCode] = watch(["skuCode"]);

  const detailProps = {
    detailRender,
    values: { ...values, skuCode },
    control,
    errors,
    setValue,
    getValues,
    onError,
  };
  const onSubmit = (data) => {
    const isSKUDuplicate = skuValidation_(details, data);
    if (isSKUDuplicate) {
      setAlert(<Error_ content="Duplicate SKU Code" onChange={setAlert} />);
      return;
    }
    if (formValues === null) {
      setDetails([...details, { ...data }]);
      reset();
      setAlert(<Success content="Added" onChange={setAlert} />);
    } else {
      setDetails(updateDetail(details, data));
      setFormValues(null);
      setAlert(<Success content="Updated" onChange={setAlert} />);
    }
  };
  const onRowClick = (values) => {
    if (formValues !== null && isDirty) {
      setDialog(
        <InfoDialog
          title={`Unsaved changes for Line No ${formValues.lineNo}`}
          content="Do you want to save the changes"
          onOk={() => {
            const data = getValues();
            setDetails(updateDetail(details, data));
            setDialog(null);
            setFormValues(values);
          }}
          onClose={() => {
            setDialog(null);
            setFormValues(values);
          }}
        />
      );
    } else {
      setFormValues(values);
    }
    return true;
  };
  const handleDelete = (data) => {
    setDetails(deleteDetail(details, data));
    setFormValues(null);
    setAlert(<Success content="Deleted" onChange={setAlert} />);
  };

  const totalQuantity = getTotalQuantity(details, "qtyOrdered");

  const Caption = <POCaption totalRows={details.length} totalQuantity={totalQuantity} />;

  return (
    <InboundHeaderCard>
      {dialog}
      {alert}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <PODetail {...detailProps} />
        {formValues === null ? (
          <InboundDetailAddButton />
        ) : (
          <InboundDetailEditButton disabled={!isDirty} onClick={() => setFormValues(null)} />
        )}
      </form>
      <BasicTable2
        columns={columns}
        caption={Caption}
        rows={details}
        onRowClick={onRowClick}
        onDelete={handleDelete}
      />
    </InboundHeaderCard>
  );
};

export default AddPO;
